import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { getInitialLanguage } from '../i18n';
import {
    SUPPORTED_LANGUAGES,
    LANG_CODE_PATTERN,
    replacePathLanguage,
    resolveStoreLanguages,
    normalizeLanguageCode,
    isRtlLanguage,
} from '../config/languages';
import { usePublicSettings } from './PublicSettingsContext';
import { useAppLanguage } from '../Hooks/useAppLanguage';

export { useAppLanguage };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const { publicSettings, loading: settingsLoading } = usePublicSettings();
    const navigate = useNavigate();
    const location = useLocation();

    const langFromPath = useMemo(() => {
        return location.pathname.match(LANG_CODE_PATTERN)?.[1];
    }, [location.pathname]);

    const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

    // Languages this store offers. Until its settings load, every bundled
    // language is allowed so we don't redirect away from a valid choice.
    const supportedLanguages = useMemo(() => {
        if (settingsLoading || !publicSettings) return SUPPORTED_LANGUAGES;
        return resolveStoreLanguages(publicSettings.supportedLocales);
    }, [settingsLoading, publicSettings]);

    // Update current language state when i18n language changes
    useEffect(() => {
        const code = normalizeLanguageCode(i18n.language) || i18n.language;
        setCurrentLanguage(code);
        document.documentElement.lang = code;
    }, [i18n.language]);

    // Sync language from URL
    useEffect(() => {
        const pathLang = normalizeLanguageCode(langFromPath);
        const active = normalizeLanguageCode(i18n.language);
        if (pathLang && SUPPORTED_LANGUAGES.includes(pathLang) && pathLang !== active) {
            i18n.changeLanguage(pathLang);
            localStorage.setItem('selectedLanguage', pathLang);
        }
    }, [langFromPath, i18n]);

    // A visitor can arrive on a language this store does not offer — move them to store's default
    useEffect(() => {
        const active = normalizeLanguageCode(i18n.language);
        if (!active || supportedLanguages.includes(active)) return;

        const fallback = supportedLanguages[0];
        if (!fallback || fallback === active) return;

        i18n.changeLanguage(fallback);
        localStorage.setItem('selectedLanguage', fallback);

        const nextPath = replacePathLanguage(location.pathname, fallback);
        if (nextPath !== location.pathname) {
            navigate(nextPath, { replace: true });
        }
    }, [supportedLanguages, i18n, navigate, location.pathname]);

    const changeLanguage = useCallback((language) => {
        const code = normalizeLanguageCode(language);
        if (!code || !supportedLanguages.includes(code)) {
            console.warn(`Unsupported language: ${language}`);
            return;
        }

        i18n.changeLanguage(code);
        localStorage.setItem('selectedLanguage', code);

        const nextPath = replacePathLanguage(location.pathname, code);
        if (nextPath !== location.pathname) {
            navigate(nextPath, { replace: true });
        }
    }, [i18n, navigate, location.pathname, supportedLanguages]);

    const dir = useCallback(() => {
        return isRtlLanguage(currentLanguage) ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    const value = {
        currentLanguage,
        changeLanguage,
        supportedLanguages,
        dir,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
